import { useState, useRef, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { FileText, Sparkles, GripVertical, X, ThumbsDown, ThumbsUp, Eye, Search, TrendingUp, Award } from "lucide-react";

const comparisonPoints = [
  { icon: Eye, before: "Ignoré en 6 secondes", after: "Retient l'attention 2 min+" },
  { icon: Search, before: "Invisible sur Google", after: "SEO optimisé, visible en 1ère page" },
  { icon: TrendingUp, before: "Aucune interaction", after: "Animations & dark mode" },
  { icon: Award, before: "Identique à 10 000 autres", after: "Unique, à votre image" },
];

const SellBeforeAfter = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [sliderPos, setSliderPos] = useState(65);
  const isDragging = useRef(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  // Auto-animate slider on first view
  useEffect(() => {
    if (!isInView) return;
    const timeout = setTimeout(() => {
      let start = 65;
      const animate = () => {
        start -= 1.5;
        if (start <= 35) { setSliderPos(35); return; }
        setSliderPos(start);
        requestAnimationFrame(animate);
      };
      animate();
    }, 800);
    return () => clearTimeout(timeout);
  }, [isInView]);

  const handleMove = (clientX: number) => {
    if (!isDragging.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(8, Math.min(92, ((clientX - rect.left) / rect.width) * 100));
    setSliderPos(x);
  };

  const startDrag = () => { isDragging.current = true; };
  const stopDrag = () => { isDragging.current = false; };

  return (
    <section className="py-24" ref={sectionRef}>
      <div className="container mx-auto px-6">
        <motion.div
          className="text-center mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            👀 La différence est flagrante
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4">
            Page basique vs{" "}
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Folio Studio
            </span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Glissez le curseur et jugez par vous-même
          </p>
        </motion.div>

        <div className="max-w-5xl mx-auto grid lg:grid-cols-[1fr_320px] gap-10 items-center">
          {/* Slider */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div
              ref={containerRef}
              className="relative rounded-2xl border border-border/50 overflow-hidden shadow-2xl select-none cursor-col-resize bg-card"
              style={{ aspectRatio: "16/10" }}
              onMouseMove={(e) => handleMove(e.clientX)}
              onMouseUp={stopDrag}
              onMouseLeave={stopDrag}
              onTouchMove={(e) => handleMove(e.touches[0].clientX)}
              onTouchEnd={stopDrag}
            >
              {/* BEFORE — Page basique */}
              <div className="absolute inset-0 bg-[hsl(var(--muted))] p-5 md:p-8">
                <div className="h-full flex flex-col bg-background rounded-xl p-5 md:p-7 shadow-inner border border-border/30">
                  {/* Header */}
                  <div className="flex items-start gap-4 mb-5 pb-5 border-b border-border/40">
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-muted flex-shrink-0" />
                    <div className="space-y-2 flex-1">
                      <div className="h-5 w-40 bg-muted rounded" />
                      <div className="h-3 w-28 bg-muted/50 rounded" />
                      <div className="h-3 w-36 bg-muted/40 rounded" />
                    </div>
                  </div>
                  {/* Sections */}
                  <div className="space-y-4 flex-1">
                    <div>
                      <div className="h-3 w-24 bg-muted/50 rounded mb-2.5 uppercase" />
                      <div className="space-y-1.5">
                        <div className="h-2.5 w-full bg-muted/25 rounded" />
                        <div className="h-2.5 w-5/6 bg-muted/25 rounded" />
                        <div className="h-2.5 w-4/6 bg-muted/25 rounded" />
                      </div>
                    </div>
                    <div>
                      <div className="h-3 w-20 bg-muted/50 rounded mb-2.5 uppercase" />
                      <div className="flex flex-wrap gap-1.5">
                        {["w-14", "w-18", "w-12", "w-16", "w-10"].map((w, i) => (
                          <div key={i} className={`h-5 ${w} bg-muted/20 rounded`} />
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="h-3 w-28 bg-muted/50 rounded mb-2.5 uppercase" />
                      <div className="space-y-1.5">
                        <div className="h-2.5 w-full bg-muted/25 rounded" />
                        <div className="h-2.5 w-3/4 bg-muted/25 rounded" />
                      </div>
                    </div>
                  </div>
                  <div className="mt-auto pt-3 flex justify-between items-center">
                    <span className="text-[10px] sm:text-xs text-muted-foreground/40 font-mono">Page 1/2</span>
                    <span className="text-[10px] sm:text-xs text-muted-foreground/40 font-mono">Généré avec Word</span>
                  </div>
                </div>
              </div>

              {/* AFTER — Folio Studio */}
              <div
                className="absolute inset-0 overflow-hidden"
                style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[hsl(222,47%,8%)] via-[hsl(222,47%,10%)] to-[hsl(210,100%,8%)] p-5 md:p-8">
                  <div className="h-full flex flex-col rounded-xl overflow-hidden border border-[hsl(var(--primary)/0.2)]">
                    {/* Browser chrome */}
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-[hsl(222,47%,12%)] border-b border-[hsl(var(--primary)/0.1)]">
                      <div className="flex gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-destructive/60" />
                        <div className="w-2.5 h-2.5 rounded-full bg-enterprise-warning/60" />
                        <div className="w-2.5 h-2.5 rounded-full bg-enterprise-success/60" />
                      </div>
                      <div className="flex-1 mx-4">
                        <div className="h-5 rounded-md bg-[hsl(222,47%,16%)] flex items-center px-3">
                          <span className="text-[10px] sm:text-xs text-[hsl(var(--primary))] font-mono">🔒 votre-nom.com</span>
                        </div>
                      </div>
                    </div>
                    {/* Content */}
                    <div className="flex-1 p-4 md:p-5 space-y-4 bg-[hsl(222,47%,9%)]">
                      {/* Nav */}
                      <div className="flex items-center justify-between">
                        <div className="h-4 w-24 bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent))] rounded" />
                        <div className="flex gap-3">
                          {[1, 2, 3, 4].map((n) => (
                            <div key={n} className="h-2 w-10 bg-[hsl(var(--primary)/0.15)] rounded" />
                          ))}
                        </div>
                      </div>
                      {/* Hero */}
                      <div className="space-y-2.5 py-2">
                        <div className="h-6 md:h-7 w-3/4 bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent))] rounded-lg opacity-90" />
                        <div className="h-3 w-1/2 bg-[hsl(var(--primary)/0.2)] rounded" />
                        <div className="flex gap-2 pt-1">
                          <div className="h-7 w-24 bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent))] rounded-lg" />
                          <div className="h-7 w-20 border border-[hsl(var(--primary)/0.3)] rounded-lg" />
                        </div>
                      </div>
                      {/* Cards */}
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { color: "from-[hsl(var(--primary)/0.2)] to-[hsl(var(--accent)/0.1)]" },
                          { color: "from-[hsl(var(--accent)/0.2)] to-[hsl(var(--primary)/0.1)]" },
                          { color: "from-[hsl(var(--primary)/0.15)] to-[hsl(var(--accent)/0.15)]" },
                        ].map((card, n) => (
                          <div key={n} className={`rounded-lg bg-gradient-to-br ${card.color} border border-[hsl(var(--primary)/0.15)] p-2.5 space-y-1.5`}>
                            <div className="w-6 h-6 rounded-md bg-[hsl(var(--primary)/0.3)]" />
                            <div className="h-1.5 w-full bg-[hsl(var(--primary)/0.15)] rounded" />
                            <div className="h-1.5 w-2/3 bg-[hsl(var(--primary)/0.1)] rounded" />
                          </div>
                        ))}
                      </div>
                      {/* Chart */}
                      <div className="flex-1 rounded-lg bg-[hsl(222,47%,11%)] border border-[hsl(var(--primary)/0.1)] p-3 flex items-end gap-1.5">
                        {[35, 55, 75, 45, 85, 65, 80, 50, 90].map((h, i) => (
                          <motion.div
                            key={i}
                            className="flex-1 rounded-t bg-gradient-to-t from-[hsl(var(--primary))] to-[hsl(var(--accent)/0.6)]"
                            initial={{ height: 0 }}
                            animate={isInView ? { height: `${h}%` } : { height: 0 }}
                            transition={{ delay: 0.5 + i * 0.08, duration: 0.6, ease: "easeOut" }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Slider handle */}
              <div
                className="absolute top-0 bottom-0 z-10"
                style={{ left: `${sliderPos}%`, transform: "translateX(-50%)" }}
                onMouseDown={startDrag}
                onTouchStart={startDrag}
              >
                <div className="w-[3px] h-full bg-primary/90 relative shadow-[0_0_12px_hsl(var(--primary)/0.4)]">
                  <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 rounded-full bg-gradient-to-br from-primary to-accent shadow-xl shadow-primary/40 flex items-center justify-center cursor-grab active:cursor-grabbing border-2 border-primary-foreground/20">
                    <GripVertical className="w-5 h-5 text-primary-foreground" />
                  </div>
                </div>
              </div>

              {/* Labels */}
              <motion.div 
                className="absolute top-4 left-4 px-3 py-2 rounded-xl bg-destructive/10 backdrop-blur-md text-xs font-bold text-destructive border border-destructive/20 z-20 flex items-center gap-1.5"
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.3 }}
              >
                <ThumbsDown className="w-3.5 h-3.5" />
                Page basique
              </motion.div>
              <motion.div
                className="absolute top-4 right-4 px-3 py-2 rounded-xl bg-primary/90 backdrop-blur-md text-xs font-bold text-primary-foreground border border-primary-foreground/20 z-20 flex items-center gap-1.5 shadow-lg shadow-primary/30"
                initial={{ opacity: 0, x: 20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.3 }}
              >
                <ThumbsUp className="w-3.5 h-3.5" />
                Folio Studio
              </motion.div>
            </div>
          </motion.div>

          {/* Comparison points */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="space-y-4"
          >
            {comparisonPoints.map((point, i) => (
              <motion.div
                key={point.after}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 + i * 0.1 }}
                className="p-4 rounded-xl bg-card/60 border border-border/50 space-y-2.5"
              >
                <div className="flex items-center gap-2 mb-1">
                  <point.icon className="w-4 h-4 text-primary" />
                  <span className="text-xs font-semibold text-foreground/70 uppercase tracking-wide">
                    {point.after.split(" ")[0]}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <X className="w-3.5 h-3.5 text-destructive flex-shrink-0" />
                  <span className="text-muted-foreground line-through text-xs">{point.before}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Sparkles className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                  <span className="text-foreground font-medium text-xs">{point.after}</span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SellBeforeAfter;
